// Các option số lượng người trúng giải có phân bổ theo vùng (chia đều cho 8 vùng)
// Khi thêm option mới (VD: 32), chỉ cần thêm vào mảng này
export const REGIONAL_QUANTITY_OPTIONS = [8, 16, 24];

// Cấu hình giải thưởng mới
export const dataTemp = {
    cfgData: {
        prizes: [],
        EACH_COUNT: [],
        COMPANY: "ShopToGo Event"
    },
    leftUsers: [],
    luckyData: {},
    t: null
}

export const usersTemp = []

// Load prizes from API
export async function loadPrizesFromAPI() {
    try {
        const { default: lotteryApi } = await import('../../../api/lottery');
        const response = await lotteryApi.getAll();
        const prizes = response.lotteries || [];

        // Sort by order
        const sortedPrizes = prizes.sort((a, b) => a.order - b.order);

        // Transform API data to lottery format
        const formattedPrizes = sortedPrizes.map((prize, index) => {
            return {
                type: index + 1,
                count: prize.quantity,
                text: prize.title,
                onlyMarker: prize.onlyMarker || false,
                spinCount: prize.spinCount
            };
        });

        // Extract EACH_COUNT from prizes
        const eachCount = sortedPrizes.map(prize => prize.spinCount);

        return {
            prizes: formattedPrizes,
            eachCount: eachCount
        };
    } catch (error) {
        console.error('Error loading prizes:', error);
        // Return default empty structure
        return {
            prizes: [],
            eachCount: []
        };
    }
}

export async function loadUsersFromAPI() {
    try {
        const { default: userLotteryApi } = await import('../../../api/userLottery');
        const response = await userLotteryApi.getAll({});
        const users = response.data || [];


        // Format users: [code, fullName, area, isMarker]
        const formattedUsers = users.map(user => [
            user.code,
            user.fullName,
            user.area,
            user.isMarker || false
        ]);

        // Separate users by marker status
        const markerUsers = formattedUsers.filter((u, idx) => users[idx].isMarker);
        const regularUsers = formattedUsers.filter((u, idx) => !users[idx].isMarker);

        return {
            allUsers: formattedUsers,
            markerUsers: markerUsers,
            regularUsers: regularUsers,
            rawUsers: users
        };
    } catch (error) {
        console.error('Error loading users:', error);
        return { allUsers: [], markerUsers: [], regularUsers: [], rawUsers: [] };
    }
}

function getAreaName(areaCode) {
    const areaNames = { '1': 'Frankfurt', '2': 'Stuttgart', '3': 'Saarbruecken', '4': 'Heidelberg' };
    return areaNames[areaCode] || 'Unknown';
}

/**
 * Extract unique regions from users array
 * Users area field is text input, so we need to normalize and group
 * @param {Array} users - Array of users [code, fullName, area, isMarker]
 * @returns {Array} Array of unique region names
 */
export function extractUniqueRegions(users) {
    if (!Array.isArray(users) || users.length === 0) {
        return [];
    }

    const regionSet = new Set();
    users.forEach(user => {
        if (user && user[2]) {
            // Normalize area name: trim and capitalize
            const normalizedArea = user[2].toString().trim();
            if (normalizedArea) {
                regionSet.add(normalizedArea);
            }
        }
    });

    return Array.from(regionSet).sort();
}

/**
 * Group users by their region
 * @param {Array} users - Array of users [code, fullName, area, isMarker]
 * @returns {Object} Object with region names as keys and arrays of users as values
 */
export function groupUsersByRegion(users) {
    if (!Array.isArray(users) || users.length === 0) {
        return {};
    }

    const grouped = {};
    users.forEach(user => {
        if (user && user[2]) {
            const normalizedArea = user[2].toString().trim();
            if (normalizedArea) {
                if (!grouped[normalizedArea]) {
                    grouped[normalizedArea] = [];
                }
                grouped[normalizedArea].push(user);
            }
        }
    });

    return grouped;
}

/**
 * Select winners ensuring equal distribution across regions
 * @param {Array} users - Array of users [code, fullName, area, isMarker]
 * @param {number} totalWinners - Total number of winners (must be in REGIONAL_QUANTITY_OPTIONS)
 * @returns {Array} Array of selected winners
 */
export function selectWinnersWithRegionalEquality(users, totalWinners) {
    if (!Array.isArray(users) || users.length === 0) {
        console.warn('No users available for lottery');
        return [];
    }

    if (!REGIONAL_QUANTITY_OPTIONS.includes(totalWinners)) {
        console.error(`Total winners must be one of: ${REGIONAL_QUANTITY_OPTIONS.join(', ')}`);
        return [];
    }

    // Group users by region
    const usersByRegion = groupUsersByRegion(users);
    const regions = Object.keys(usersByRegion);

    if (regions.length === 0) {
        console.warn('No regions found in user data');
        return [];
    }

    // Calculate winners per region
    const winnersPerRegion = totalWinners / 8;

    // If we have exactly 8 regions, perfect distribution
    if (regions.length === 8) {
        return selectFromExact8Regions(usersByRegion, regions, winnersPerRegion);
    }

    // If we have more than 8 regions, select top 8 regions by employee count
    if (regions.length > 8) {
        return selectFromMoreThan8Regions(usersByRegion, winnersPerRegion);
    }

    // If we have less than 8 regions, cannot guarantee equal distribution
    console.warn(`Only ${regions.length} regions found, need 8 for equal distribution`);
    return selectFromLessThan8Regions(usersByRegion, regions, totalWinners);
}

/**
 * Select winners from exactly 8 regions
 */
function selectFromExact8Regions(usersByRegion, regions, winnersPerRegion) {
    const winners = [];

    // Collect all winners from each region first
    regions.forEach(region => {
        const regionUsers = usersByRegion[region];
        const selectedFromRegion = selectRandomUsers(regionUsers, winnersPerRegion);
        winners.push(...selectedFromRegion);
    });

    // Shuffle the final winners array to randomize display order
    // This ensures equal distribution but random appearance
    for (let i = winners.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [winners[i], winners[j]] = [winners[j], winners[i]];
    }

    return winners;
}

/**
 * Select winners from more than 8 regions (pick top 8 by employee count)
 */
function selectFromMoreThan8Regions(usersByRegion, winnersPerRegion) {
    // Sort regions by employee count (descending)
    const sortedRegions = Object.keys(usersByRegion).sort((a, b) => {
        return usersByRegion[b].length - usersByRegion[a].length;
    });

    // Take top 8 regions
    const top8Regions = sortedRegions.slice(0, 8);

    const winners = [];
    top8Regions.forEach(region => {
        const regionUsers = usersByRegion[region];
        const selectedFromRegion = selectRandomUsers(regionUsers, winnersPerRegion);
        winners.push(...selectedFromRegion);
    });

    // Shuffle the final winners array to randomize display order
    for (let i = winners.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [winners[i], winners[j]] = [winners[j], winners[i]];
    }

    return winners;
}

/**
 * Select winners from less than 8 regions (distribute as evenly as possible)
 */
function selectFromLessThan8Regions(usersByRegion, regions, totalWinners) {
    const winners = [];
    const baseWinnersPerRegion = Math.floor(totalWinners / regions.length);
    const remainder = totalWinners % regions.length;

    regions.forEach((region, index) => {
        const regionUsers = usersByRegion[region];
        // First 'remainder' regions get one extra winner
        const winnersForThisRegion = baseWinnersPerRegion + (index < remainder ? 1 : 0);
        const selectedFromRegion = selectRandomUsers(regionUsers, winnersForThisRegion);
        winners.push(...selectedFromRegion);
    });

    // Shuffle the final winners array to randomize display order
    for (let i = winners.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [winners[i], winners[j]] = [winners[j], winners[i]];
    }

    return winners;
}

/**
 * Select random users from an array
 * @param {Array} users - Array of users
 * @param {number} count - Number of users to select
 * @returns {Array} Array of selected users
 */
function selectRandomUsers(users, count) {
    if (!users || users.length === 0) {
        return [];
    }

    if (users.length <= count) {
        // Not enough users, return all
        return [...users];
    }

    // Fisher-Yates shuffle and take first 'count' items
    const shuffled = [...users];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled.slice(0, count);
}
